import { useRef } from 'react';
import type { FolderAnalysisProgress, SetupDesignSnapshot } from '../shared/desktop-api';
import { describeSetupDesign, type SetupDesignView } from './setup-design-progress';

/** A snapshot is new when the engine took it at a different moment or with different counts; a heartbeat repeats the last one. */
const snapshotKey = (design: SetupDesignSnapshot) =>
  [
    design.elapsedMilliseconds,
    design.questionsAnswered,
    design.questionsAsking,
    design.failures ?? 0,
    design.types.map((type) => `${type.state}:${type.fields.length}`).join(','),
  ].join('|');

/**
 * PAIGE: the field design's view for the setup screen, from the newest progress the engine
 * sent. The snapshot's own silence and estimate run on from the moment this screen first
 * saw it, so they keep moving between snapshots (and through heartbeats, which repeat the
 * last one). Null outside the field design.
 */
export function useSetupDesign(
  progress: FolderAnalysisProgress | null | undefined,
  now: number,
): SetupDesignView | null {
  const seen = useRef<{ key: string; at: number } | null>(null);
  const activity = progress?.activity;
  const design = activity?.id === 'bootstrap:fields' ? activity.design : null;
  if (!design || progress?.stage !== 'understanding') return null;
  const key = snapshotKey(design);
  if (seen.current?.key !== key) seen.current = { key, at: now };
  return describeSetupDesign(design, now - seen.current.at);
}
